import { useEffect, useState } from "react";

import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { ConfirmDialog } from "@/pages/tina/festivals/components/confirm-dialog";
import type {
  PageForm,
  PageFormAction,
} from "@/pages/tina/festivals/lib/page-form";
import type { FestivalPageRead } from "@/types/tina-festival-page";

interface OwnPageDialogProps {
  form: PageForm;
  page: FestivalPageRead;
  dispatch: (action: PageFormAction) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const NONE = "__none__";

/** Mark which page is the festival's own: one of the member pages, or none. Nothing is sent until the next approval. */
export function OwnPageDialog({
  form,
  page,
  dispatch,
  open,
  onOpenChange,
}: OwnPageDialogProps) {
  const current =
    Object.entries(form.pages).find(([, choice]) => choice.ownPage)?.[0] ??
    NONE;
  const [picked, setPicked] = useState(current);
  useEffect(() => {
    if (open) setPicked(current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const members = page.pages.filter((read) => form.pages[read.url]?.member);

  return (
    <ConfirmDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Mark the festival's own page"
      description="Sent with your next Approve and publish or Publish changes."
      confirmLabel="Use this page"
      cancelLabel="Cancel"
      confirmVariant="default"
      onConfirm={() =>
        dispatch({ type: "set_own_page", url: picked === NONE ? null : picked })
      }
    >
      <RadioGroup value={picked} onValueChange={setPicked}>
        {members.map((read) => (
          <div key={read.url} className="flex items-center gap-2">
            <RadioGroupItem value={read.url} id={`own-${read.url}`} />
            <Label htmlFor={`own-${read.url}`}>
              {read.site} · {read.url.replace(/^[a-z]+:\/\//i, "")}
            </Label>
          </div>
        ))}
        <div className="flex items-center gap-2">
          <RadioGroupItem value={NONE} id="own-none" />
          <Label htmlFor="own-none">None</Label>
        </div>
      </RadioGroup>
    </ConfirmDialog>
  );
}
